import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleFilledIcon } from '@/src/renderer/components/icons'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { PeriodIcon, ClockSmallIcon } from './WhenDropdownIcons'
import { PERIODS, isToday, isTomorrow, isPeriodPastToday } from './whenDropdownUtils'
import { PERIOD_RANGES } from '../../utils/parseStartDate'
import type { StartPeriod } from '@/src/renderer/lib/db/models'
import type { StartValue } from './WhenDropdown.types'
// Die Klassen `when-dropdown__…` stehen in WhenDropdown.scss — auch, wenn nur Deadline diesen Teil nutzt.
import './WhenDropdown.scss'

interface PeriodOptionsProps {
  selectedDate: Date | null
  selectedPeriod: StartPeriod | null
  selectedTime: string
  timeSource: 'period' | 'time' | null
  onPeriodSelect: (period: StartPeriod) => void
  onValueChange: (value: StartValue) => void
  onClose: () => void
}

export const PeriodOptions: React.FC<PeriodOptionsProps> = ({
  selectedDate,
  selectedPeriod,
  timeSource,
  onPeriodSelect,
  onValueChange,
  onClose,
}) => {
  const { t } = useTranslation()

  const getPeriodPrefix = (period: StartPeriod): string => {
    const effectiveDate = selectedDate ?? new Date()
    const isPast = isPeriodPastToday(period)

    if (!isToday(effectiveDate) && !isTomorrow(effectiveDate)) return ''
    if (isToday(effectiveDate) && isPast) return t('taskCreation.whenTomorrow') + ' '
    if (isToday(effectiveDate)) return t('taskCreation.whenToday') + ' '
    if (isTomorrow(effectiveDate)) return t('taskCreation.whenTomorrow') + ' '
    return ''
  }

  return (
    <>
      {PERIODS.map(({ key, shortcut }) => {
        const range = PERIOD_RANGES[key]
        const isSelected = selectedPeriod === key && timeSource === 'period'
        const periodPrefix = getPeriodPrefix(key)
        return (
          <button
            key={key}
            type="button"
            className={`when-dropdown__item${isSelected ? ' when-dropdown__item--selected' : ''}`}
            onClick={() => onPeriodSelect(key)}
            onDoubleClick={() => {
              const date = selectedDate ?? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })()
              onValueChange({ date, time: null, period: key })
              onClose()
            }}
          >
            <PeriodIcon period={key} size={16} />
            <span className="when-dropdown__item-text">
              {periodPrefix}{t(`taskCreation.whenPeriod_${key}`)}
            </span>
            <div className="when-dropdown__item-right">
              {isSelected && (
                <CheckCircleFilledIcon size={16} className="when-dropdown__check" />
              )}
              <span className="when-dropdown__time-range">
                <ClockSmallIcon />
                {range.start.replace(':00', '')}–{range.end.replace(':00', '')}
              </span>
              <ShortcutHint>{shortcut}</ShortcutHint>
            </div>
          </button>
        )
      })}
    </>
  )
}
